




















































































































import dayjs from 'dayjs';
import * as R from 'ramda';
import { Locale } from 'vue-i18n';
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import { _Boolean } from '@/API';
import { JobItem } from '@/appsync/graphql/job';
import { fetchScheduledEventHistoriesForTargetMonth } from '@/appsync/service/EventCalendarQueryService';
import EventItem from '@/components/molecules/EventItem.vue';
import MessageDialog from '@/components/molecules/MessageDialog.vue';
import { i18n } from '@/plugins/i18n/i18n';
import {
  LoginInfo,
  ScheduledHolidayMetadataForFront,
  ScheduledEventHistoryForFront,
  PublicHolidayForFront,
  ScheduledEventMetadataWithDetailForFront,
  CalendarEvent,
} from '@/types/type';
import { isToday } from '@/utils/CommonUtils';
import {
  EventType,
  FAILED_TO_ADJUST_DATE_EVENT_ID_KEY,
  filterDuplicatedEvents,
  getTargetMonthItems,
  makeAdjustments,
  occurrencesForTargetMonth,
  eventIcon,
  eventIconColor,
} from '@/utils/EventCalendarUtils';
import { newPortalDate } from '@/utils/PortalDate';
import { Language, ICON, TriggerOnHolidays, DialogType } from '@/utils/consts';

export type TaskHistory = {
  order: number;
  log: string;
  taskExecutionStatus: TaskExecutionStatus;
  task: Task;
  includeInDownload: _Boolean;
};

type Task = {
  title: string;
};

type EventDate = {
  date: string;
  month: number;
  day: number;
};

const defaultSelectedEventDate: EventDate = {
  date: '',
  month: 0,
  day: 0,
};

export enum JobExecutionStatus {
  NotTriggered = 'NotTriggered',
  Running = 'Running',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  Aborted = 'Aborted',
}

export enum TaskExecutionStatus {
  Running = 'Running',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  Skipped = 'Skipped',
}

const MINS_IN_AN_HOUR = 60;

@Component({
  components: { EventItem, MessageDialog },
})
export default class EventCalendarPanel extends Vue {
  @Prop()
  private loginInfo!: LoginInfo;
  @Prop()
  private scheduledHolidayMetadatas!: ScheduledHolidayMetadataForFront[];
  @Prop()
  private scheduledEventMetadatas!: ScheduledEventMetadataWithDetailForFront[];
  @Prop()
  private publicHolidays!: PublicHolidayForFront[];

  private isFirstExecution: boolean = true;
  private loading: boolean = true;
  private focusDate: string = '';
  private events: CalendarEvent[] = [];
  private scheduledEventHistoriesForTargetMonth!: ScheduledEventHistoryForFront[];
  private eventsForSelectedDay: CalendarEvent[] = [];
  private selectedOpen: boolean = false;
  private selectedEventDateElement: HTMLElement | null = null;
  private selectedEventDate: EventDate = defaultSelectedEventDate;
  private allHolidayEvents: CalendarEvent[] = [];
  private moreThan1000ConsecutiveHolidayContainingEvents: any[] = [];
  private dialogType: DialogType = DialogType.Warning;

  public async created(): Promise<void> {
    this.focusDate = this.getFocusDate();
    await this.fetchEvents();
    this.loading = false;
  }

  private getFocusDate() {
    return !this.$route.params.year ||
      !this.$route.params.month ||
      !this.$route.params.day
      ? newPortalDate(dayjs(), 'YYYY-MM-DD').toString()
      : `${this.$route.params.year}-${this.$route.params.month}-${this.$route.params.day}`;
  }
  private async fetchEvents(): Promise<void> {
    // step: 1.month A has events and click prev or next button to change the month
    // → 2.month B is displayed and does not have any event
    // → 3.back to month A, will be displayed without any event
    // It seems like a bug of vuetify library. Need to take more time to research the reason and fix this.
    // Temporarily set the events' length as 1 to ensure the events will not be empty.
    // TODO:http://product-ci.works-hi/company/hr-portal/-/issues/619
    this.events.length = 1;
    await this.fetchScheduledEventHistoriesForTargetMonth();
    this.getPublicHolidayEvents();
    this.getCustomHolidayEvents();
    this.getNormalEvents();
  }

  private async fetchScheduledEventHistoriesForTargetMonth() {
    this.scheduledEventHistoriesForTargetMonth = await fetchScheduledEventHistoriesForTargetMonth(
      this.loginInfo.tenant,
      this.loginInfo.landscape,
      this.focusDate
    );
  }

  private getPublicHolidayEvents() {
    const publicHolidayEvents: CalendarEvent[] = [];
    this.publicHolidays.forEach(publicHoliday => {
      const event = {
        eventType: EventType.PublicHoliday,
        name: publicHoliday.title,
        title: publicHoliday.title,
        start: publicHoliday.startDate,
        labels: [publicHoliday.label],
      };
      publicHolidayEvents.push(event);
    });
    this.allHolidayEvents.push(...publicHolidayEvents);
    this.events.push(...this.sortEventByName(publicHolidayEvents));
  }

  private getCustomHolidayEvents() {
    const customHolidayEvents: CalendarEvent[] = [];
    this.scheduledHolidayMetadatas.forEach(scheduledHolidayMetadata => {
      const scheduledHolidayRruleSet = scheduledHolidayMetadata.rruleSet;
      const holidayStartDateTimeList = occurrencesForTargetMonth(
        scheduledHolidayRruleSet,
        this.focusDate
      );
      if (holidayStartDateTimeList) {
        holidayStartDateTimeList.forEach(holidayStartDateTime => {
          const event = {
            eventType: EventType.CustomHoliday,
            name: scheduledHolidayMetadata.title,
            title: scheduledHolidayMetadata.title,
            start: newPortalDate(holidayStartDateTime, 'YYYY-MM-DD').toString(),
            rruleSet: scheduledHolidayRruleSet,
            eventId: scheduledHolidayMetadata.id,
            labels: scheduledHolidayMetadata.labels,
          };
          customHolidayEvents.push(event);
        });
      }
    });
    this.allHolidayEvents.push(...customHolidayEvents);
    const thisMonthHolidays = getTargetMonthItems(
      customHolidayEvents,
      this.focusDate
    );
    this.events.push(...this.sortEventByName(thisMonthHolidays));
  }

  private getNormalEvents() {
    const normalEvents: CalendarEvent[] = [];
    this.scheduledEventMetadatas.forEach(scheduledEventMetadata => {
      const scheduledEventRruleSet = scheduledEventMetadata.rruleSet;
      const scheduledEventStartDateTimeList = occurrencesForTargetMonth(
        scheduledEventRruleSet,
        this.focusDate
      );
      if (scheduledEventStartDateTimeList) {
        scheduledEventStartDateTimeList.forEach(scheduledEventStartDateTime => {
          const startYYYYMMDDHHmmss = newPortalDate(
            scheduledEventStartDateTime,
            'YYYY-MM-DD HH:mm:ss'
          ).toString();

          const event = this.generateEventWithoutEventHistory(
            startYYYYMMDDHHmmss,
            scheduledEventMetadata,
            scheduledEventRruleSet
          );
          normalEvents.push(event);
        });
      }
    });

    const adjustedNormalEvents = normalEvents.map(event =>
      makeAdjustments(event, this.allHolidayEvents)
    );

    this.moreThan1000ConsecutiveHolidayContainingEvents = adjustedNormalEvents.filter(
      event =>
        event && Object.keys(event).includes(FAILED_TO_ADJUST_DATE_EVENT_ID_KEY)
    );

    const nonNullAndNotFailedAdjustedNormalEvents = adjustedNormalEvents.filter(
      event =>
        event &&
        !Object.keys(event).includes(FAILED_TO_ADJUST_DATE_EVENT_ID_KEY)
    ) as CalendarEvent[];

    const filteredDuplicateNormalEvents = filterDuplicatedEvents(
      nonNullAndNotFailedAdjustedNormalEvents
    ) as CalendarEvent[];

    const eventsBasedOnScheduledEventHistory = this.getEventsBasedOnScheduledEventHistory();
    const normalEventsWithEventsBasedOnScheduledEventHistory = this.combineFilteredDuplicateNormalEventsWithEventsBasedOnScheduledEventHistory(
      filteredDuplicateNormalEvents,
      eventsBasedOnScheduledEventHistory
    );
    const thisMonthEvents = getTargetMonthItems(
      normalEventsWithEventsBasedOnScheduledEventHistory,
      this.focusDate
    );
    this.events.push(...this.sortEventByName(thisMonthEvents));
  }

  private get showMoreThan1000ConsecutiveHolidayExistsMessage(): boolean {
    return this.moreThan1000ConsecutiveHolidayContainingEvents.length > 0;
  }

  private moreThan1000ConsecutiveHolidayDialogTitle() {
    return this.$t(
      'jobScheduler.eventCalendar.moreThan1000ConsecutiveHolidayDialogTitle'
    );
  }

  private get editEventPageForEventWith1000ConsecutiveHolidays() {
    return {
      name: 'editEvent',
      params: {
        id: this.moreThan1000ConsecutiveHolidayContainingEvents[0]
          ? R.prop(
              FAILED_TO_ADJUST_DATE_EVENT_ID_KEY,
              this.moreThan1000ConsecutiveHolidayContainingEvents[0]
            )
          : '',
      },
    };
  }
  private sortEventByName(event: CalendarEvent[]): CalendarEvent[] {
    const intlCollator = new Intl.Collator(this.locale);
    return event.sort((x, y) => {
      return intlCollator.compare(x.name, y.name);
    });
  }

  private generateEventWithoutEventHistory(
    startYYYYMMDDHHmmss: string,
    scheduledEventMetadata: ScheduledEventMetadataWithDetailForFront,
    rruleSet: string
  ): CalendarEvent {
    const status = JobExecutionStatus.NotTriggered;
    const title = scheduledEventMetadata.title;
    return {
      eventType: EventType.NormalEvent,
      name: this.concatStartTimeWithTitle(startYYYYMMDDHHmmss, title),
      title,
      start: startYYYYMMDDHHmmss,
      rruleSet,
      eventId: scheduledEventMetadata.id,
      status,
      jobId: scheduledEventMetadata.job ? scheduledEventMetadata.job.id : '',
      jobTitle: scheduledEventMetadata.job
        ? scheduledEventMetadata.job.title
        : '',
      hasOutputFile: scheduledEventMetadata.job
        ? scheduledEventMetadata.job.hasOutputFile
        : false,
      icon: eventIcon(status),
      iconColor: eventIconColor(status),
      labels: scheduledEventMetadata.customHolidayLabels,
      holidayExecutionType: scheduledEventMetadata.holidayExecutionType,
      triggerOnHolidays: scheduledEventMetadata.triggerOnHolidays,
      isEventWithoutScheduledEventMetadata: false,
    };
  }

  private generateEventWithoutScheduledEventMetadata(
    scheduledEventHistoryForTargetMonth: ScheduledEventHistoryForFront,
    status: JobExecutionStatus,
    title: string,
    taskHistories: TaskHistory[] | undefined,
    start: string
  ): CalendarEvent {
    return {
      eventType: EventType.NormalEvent,
      name: this.concatStartTimeWithTitle(start, title),
      title,
      start,
      eventId: scheduledEventHistoryForTargetMonth.scheduledEventMetadataId,
      jobId: scheduledEventHistoryForTargetMonth.job
        ? scheduledEventHistoryForTargetMonth.job.id
        : '',
      jobTitle: scheduledEventHistoryForTargetMonth.job
        ? scheduledEventHistoryForTargetMonth.job.title
        : '',
      hasOutputFile: scheduledEventHistoryForTargetMonth.job
        ? scheduledEventHistoryForTargetMonth.job.hasOutputFile
        : false,
      icon: eventIcon(status),
      iconColor: eventIconColor(status),
      eventHistoryId: scheduledEventHistoryForTargetMonth.id,
      status,
      taskHistories,
      isEventWithoutScheduledEventMetadata: true,
    };
  }

  private combineFilteredDuplicateNormalEventsWithEventsBasedOnScheduledEventHistory(
    filteredDuplicateNormalEvents: CalendarEvent[],
    eventsBasedOnScheduledEventHistory: CalendarEvent[]
  ): CalendarEvent[] {
    const normalEventsWithEventsBasedOnEventHistory = R.concat(
      eventsBasedOnScheduledEventHistory,
      filteredDuplicateNormalEvents
    );
    return filterDuplicatedEvents(normalEventsWithEventsBasedOnEventHistory);
  }

  private getEventsBasedOnScheduledEventHistory(): any {
    return this.scheduledEventHistoriesForTargetMonth.map(
      scheduledEventHistoryForTargetMonth => {
        const scheduledEventMetadata = this.scheduledEventMetadatas.find(
          item =>
            scheduledEventHistoryForTargetMonth.scheduledEventMetadataId ===
            item.id
        );
        const status = scheduledEventHistoryForTargetMonth.jobExecutionStatus;
        const title = scheduledEventHistoryForTargetMonth.title;
        const executedBy = scheduledEventHistoryForTargetMonth.executedBy;
        const updatedAt = scheduledEventHistoryForTargetMonth.updatedAt;
        const taskHistories: TaskHistory[] | undefined = R.path(
          ['taskHistories', 'items'],
          scheduledEventHistoryForTargetMonth
        );
        const start = newPortalDate(
          scheduledEventHistoryForTargetMonth.eventStartDateTime,
          'YYYY-MM-DD HH:mm:ss'
        ).toString();
        if (!scheduledEventMetadata) {
          return this.generateEventWithoutScheduledEventMetadata(
            scheduledEventHistoryForTargetMonth,
            status,
            title,
            taskHistories,
            start
          );
        }
        return {
          eventType: EventType.NormalEvent,
          name: this.concatStartTimeWithTitle(start, title),
          title,
          start,
          rruleSet: scheduledEventMetadata.rruleSet,
          eventId: scheduledEventMetadata.id,
          jobId: scheduledEventMetadata.job
            ? scheduledEventMetadata.job.id
            : '',
          jobTitle: scheduledEventMetadata.job
            ? scheduledEventMetadata.job.title
            : '',
          hasOutputFile: scheduledEventMetadata.job
            ? scheduledEventMetadata.job.hasOutputFile
            : false,
          icon: eventIcon(status),
          iconColor: eventIconColor(status),
          labels: scheduledEventMetadata.customHolidayLabels,
          holidayExecutionType: scheduledEventMetadata.holidayExecutionType,
          triggerOnHolidays: scheduledEventMetadata.triggerOnHolidays,
          eventHistoryId: scheduledEventHistoryForTargetMonth.id,
          status,
          taskHistories,
          isEventWithoutScheduledEventMetadata: false,
          executedBy,
          updatedAt,
        };
      }
    );
  }

  private async prev(): Promise<void> {
    const calendar = this.$refs.calendar as any;
    calendar.prev();
    await this.fetchEvents();
  }

  private async next(): Promise<void> {
    const calendar = this.$refs.calendar as any;
    calendar.next();
    await this.fetchEvents();
  }

  private async setToday() {
    this.focusDate = newPortalDate(dayjs(), 'YYYY-MM-DD').toString();
    await this.fetchEvents();
  }

  private get locale(): string {
    const locale = i18n.locale as Language;
    switch (locale) {
      case Language.ja:
        return 'ja-jp';
      case Language.en:
        return 'en-us';
      default: {
        const inspector: never = locale;
        return 'ja-jp';
      }
    }
  }

  private get monthText(): string {
    const locale = i18n.locale as Language;
    switch (locale) {
      case Language.ja:
        return newPortalDate(dayjs(this.focusDate), 'YYYY年 M月').toString();
      case Language.en:
        return newPortalDate(dayjs(this.focusDate), 'MMMM YYYY').toString();
      default: {
        const inspector: never = locale;
        return newPortalDate(dayjs(this.focusDate), 'YYYY年 M月').toString();
      }
    }
  }

  private getDayText(eventDate: EventDate): string | number {
    if (isToday(eventDate.date) || this.isNotFirstDay(eventDate.day)) {
      return eventDate.day;
    }
    const locale = i18n.locale as Language;
    switch (locale) {
      case Language.ja:
        return newPortalDate(dayjs(eventDate.date), 'M月 D日').toString();
      case Language.en:
        return newPortalDate(dayjs(eventDate.date), 'MMM D').toString();
      default: {
        const inspector: never = locale;
        return newPortalDate(dayjs(eventDate.date), 'M月 D日').toString();
      }
    }
  }

  private isToday_ = isToday;

  private isNotFirstDay(day: number): boolean {
    return day !== 1;
  }

  private concatStartTimeWithTitle(start: string, name: string): string {
    return `${newPortalDate(dayjs(start), 'HH:mm').toString()} ${name}`;
  }

  private showEventsForSelectedDay(eventDate: EventDate) {
    const eventsForSelectedDay = this.events.filter(
      item =>
        newPortalDate(dayjs(item.start), 'YYYY-MM-DD').toString() ==
        eventDate.date
    );
    this.eventsForSelectedDay = eventsForSelectedDay.sort((a, b) => {
      return dayjs(a.start).valueOf() - dayjs(b.start).valueOf();
    });

    const open = () => {
      this.selectedEventDate = eventDate;
      this.selectedEventDateElement = this.$refs[
        `childDayLabel${eventDate.date}`
      ] as HTMLElement;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => (this.selectedOpen = true))
      );
    };

    if (this.selectedOpen) {
      this.selectedOpen = false;
      requestAnimationFrame(() => requestAnimationFrame(() => open()));
    } else {
      open();
    }
  }
}
